import { useTranslations } from '@aikoa/i18n/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import { InviteTeamMemberBox } from '~/components/home/InviteTeamMemberBox'
import ContentBox from '~/components/layout/ContentBox'
import { ViewTitle } from '~/components/layout/Sections'
import { Body } from '~/components/typography/Content'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { LinkButton } from '~/components/ui/button'
import { type ContactMethodsChangeEvent } from '~/components/user/ContactMethodsManager'
import { InvoicingDetailsView } from '~/components/user/invoicing/InvoicingDetailsView'
import { ProfileHeader } from '~/components/user/ProfileHeader'
import { InboundEmailView } from '~/components/user/realtors/InboundEmailView'
import { OrganisationBox } from '~/components/user/realtors/OrganisationBox'
import RealtorProfileForm, {
  type RealtorProfileFormValues,
} from '~/components/user/realtors/RealtorProfileForm'
import { useSession } from '~/components/user/SessionProvider'
import { useUpdateUserProfile, useUserProfile } from '~/data/user/userProfile'
import { type UpdateUserProfilePayload } from '~/data/user/writeUserProfile'
import { cn } from '~/lib/utils'

type ProfileViewTab = 'organisation' | 'invoicing' | 'inbound-email'

interface ProfileViewProps {
  showCompletionStatus?: boolean
  className?: string
}

function RealtorProfileView({ showCompletionStatus = true, className }: ProfileViewProps) {
  const { data: profile, isLoading: isProfileLoading } = useUserProfile()
  const { t } = useTranslations('user')

  const { session, isAnonymous } = useSession()
  const user = session?.user

  const updateUserProfileMutation = useUpdateUserProfile()
  const didUpdateProfile = useRef(false)

  useEffect(() => {
    if (
      !user ||
      isProfileLoading ||
      isAnonymous ||
      !profile ||
      didUpdateProfile.current ||
      updateUserProfileMutation.isPending
    ) {
      return
    }

    const profileName = profile?.name
    const userMetadataName = user?.user_metadata?.name

    const needsNameUpdate = !profileName && userMetadataName

    if (needsNameUpdate) {
      updateUserProfileMutation.mutate({
        user_id: user.id,
        name: needsNameUpdate ? userMetadataName : undefined,
      })

      didUpdateProfile.current = true
    }
  }, [user, isProfileLoading, isAnonymous, profile, updateUserProfileMutation])

  const initialValues = useMemo(() => {
    if (!profile) {
      return {}
    }

    return {
      name: profile.name ?? '',
      title: profile.data?.title ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phone ?? '',
      avatarUrl: profile.data?.avatar_url ?? '',
    }
  }, [profile])

  const [currentValues, setCurrentValues] = useState<RealtorProfileFormValues | null>(null)

  const debouncedUpdateProfile = useDebouncedCallback(
    (profileData: Omit<UpdateUserProfilePayload, 'user_id'>) => {
      if (profile?.user_id) {
        updateUserProfileMutation.mutate({
          user_id: profile.user_id,
          ...profileData,
        })
      }
    },
    500,
    {
      leading: false,
      trailing: true,
    }
  )

  const handleValuesChange = (values: RealtorProfileFormValues, _isValid: boolean) => {
    const previousValues = currentValues
    setCurrentValues(values)

    if (!previousValues || !profile?.user_id) {
      return
    }

    const changes: Partial<UpdateUserProfilePayload> = {}

    if (values.name !== previousValues.name) {
      changes.name = values.name
    }

    if (values.title !== previousValues.title) {
      changes.title = values.title
    }

    if (values.email !== previousValues.email) {
      changes.email = values.email
    }

    if (values.phoneNumber !== previousValues.phoneNumber) {
      changes.phoneNumber = values.phoneNumber
    }

    if (values.avatarUrl !== previousValues.avatarUrl) {
      changes.avatarUrl = values.avatarUrl
    }

    if (Object.keys(changes).length > 0) {
      debouncedUpdateProfile(changes)
    }
  }

  const handleContactMethodsChange = (values: ContactMethodsChangeEvent) => {
    if (!profile?.user_id) {
      return
    }

    updateUserProfileMutation.mutate({
      user_id: profile.user_id,
      email: values.email,
      phoneNumber: values.phoneNumber,
    })
  }

  const [activeTab, setActiveTab] = useState<ProfileViewTab>('organisation')

  const tabs = useMemo((): TabItem<ProfileViewTab>[] => {
    return [
      {
        id: 'organisation',
        label: t(($) => $.profile.realtor.tabs.organisation),
        animationOrigin: 'top-center',
        content: () => <OrganisationBox className="rounded-tl-none" />,
      },
      {
        id: 'invoicing',
        label: t(($) => $.profile.realtor.tabs.invoicing),
        animationOrigin: 'top-left',
        content: () => (
          <ContentBox scrollable={false} contentContainerClassName="gap-4 px-0 pb-0 pt-0">
            <InvoicingDetailsView />
          </ContentBox>
        ),
      },
      {
        id: 'inbound-email',
        label: t(($) => $.profile.realtor.tabs.inboundEmail),
        animationOrigin: 'top-right',
        content: () => (
          <ContentBox
            scrollable={false}
            className="rounded-tr-none"
            contentContainerClassName="gap-4 px-0 pt-0"
            header={
              <ViewTitle className="px-4 pb-4 pt-6">
                {t(($) => $.profile.realtor.inboundEmail.title)}
              </ViewTitle>
            }>
            <Body className="px-6 text-center text-base/normal text-secondary">
              {t(($) => $.profile.realtor.inboundEmail.description)}
            </Body>

            <InboundEmailView className="px-6" />
          </ContentBox>
        ),
      },
    ]
  }, [t])

  return (
    <View className={cn('flex-1 gap-3', className)}>
      <ContentBox
        scrollable={false}
        contentContainerClassName="gap-4 px-0"
        header={<ProfileHeader />}>
        <RealtorProfileForm
          initialValues={initialValues}
          onValuesChange={handleValuesChange}
          onContactMethodsChange={handleContactMethodsChange}
          showCompletionStatus={showCompletionStatus}
          showAvatarUpload
          className={className}
        />
      </ContentBox>
      <View className="min-h-[250px]">
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
          <TabBar tabButtonTextClassName="font-normal" tabButtonCountTextClassName="" />
          <TabContent tabContentClassName="gap-3" />
        </Tabs>
      </View>
      <ContentBox
        scrollable={false}
        contentContainerClassName="items-center gap-4 px-6"
        header={
          <ViewTitle className="px-4 pb-2 pt-6">
            {t(($) => $.messagePreviews.profileCta.title)}
          </ViewTitle>
        }>
        <Body className="text-center text-base/normal text-secondary">
          {t(($) => $.messagePreviews.profileCta.description)}
        </Body>
        <LinkButton href="/(authenticated)/message-previews" variant="outline" size="lg">
          {t(($) => $.messagePreviews.profileCta.button)}
        </LinkButton>
      </ContentBox>
      <InviteTeamMemberBox />
    </View>
  )
}

export default RealtorProfileView
